import { Injectable } from "@angular/core";
import { ConciergePatientLoginData } from "@dentally/auth/auth-types";
import { catchError, lastValueFrom, Observable, tap } from "rxjs";
import { StatusCodes } from "http-status-codes";
import { WindowService } from "@apps/patient-core/src/app/services/window.service";
import { CacheService } from "@apps/patient-core/src/app/services/cache.service";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { logger } from "@dentally/ui/logger";
import { ConciergeAuthHttpService } from "./derived/auth-http.service";
import { ConciergeDataService } from "./concierge-data.service";

@Injectable({
  providedIn: "root",
})
export class ConciergeSpecificAuthService {
  constructor(
    private _authService: BaseAuthService,
    private _authHttpService: ConciergeAuthHttpService,
    private _cacheService: CacheService,
    private _windowService: WindowService,
    private _conciergeDataService: ConciergeDataService
  ) {}

  public login(payload: Omit<ConciergePatientLoginData, "stage" | "region">): Observable<{ accessToken: string } | { success: boolean; locked: boolean }> {
    const conciergeData = this._conciergeDataService.getConciergeData();

    if (!conciergeData) {
      throw new Error("No concierge data found");
    }

    const { stage, region } = conciergeData;

    return this._authHttpService.login({
      ...payload,
      stage,
      region,
    });
  }

  public handleDeviceLinked(jwt: string, nonce: string): Observable<{ accessToken: string }> {
    return this._authHttpService.handleDeviceLinked(jwt, nonce);
  }

  public getNewSession(): Promise<{ accessToken: string } | void> {
    return lastValueFrom(
      this._authHttpService.getNewSession().pipe(
        catchError((error) => {
          if ([StatusCodes.UNAUTHORIZED, StatusCodes.FORBIDDEN].includes(error.status as StatusCodes)) {
            logger.error("Unauthorized or forbidden error getting new session", {
              status: error.status,
              statusText: error.statusText,
            });
            return this._authHttpService.delete().pipe(
              tap(() => {
                this._unpairDevice();
              })
            );
          }

          throw error;
        }),
        tap((response) => {
          if (response && "accessToken" in response) {
            this._authService.setToken(response.accessToken);
          }
        })
      )
    );
  }

  public delete(): Promise<void> {
    return lastValueFrom(this._authHttpService.delete());
  }

  public async logout(trigger: string): Promise<{ accessToken: string }> {
    this._authService.accessToken = "";

    return lastValueFrom(
      this._authHttpService.logout(trigger).pipe(
        tap((response) => {
          this._authService.setToken(response.accessToken);
        })
      )
    );
  }

  private _unpairDevice(): void {
    this._authService.delete();
    this._cacheService.clearAll();
    this._windowService.open("/");
  }
}
